import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Badge } from "react-bootstrap";
import { FaEllipsisV } from "react-icons/fa";
import { request } from "../../../utils/api";
import { toast } from "react-toastify";
import Loading from "../utilComponent/Loading";
import Pagination from "../utilComponent/Pagination";

const STATUS_COLORS = {
  Pending: "secondary",
  Progress: "warning",
  Done: "success",
  Rejected: "danger",
};

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 12,
    total: 0,
    last_page: 1,
  });

  const getOrderList = async (page = 1) => {
    try {
      setLoading(true);
      const response = await request({
        url: `orders?page=${page}&per_page=${pagination.per_page}`,
        method: "GET",
      });

      if (response.status && response.data) {
        setOrders(
          response.data.items.map((item) => ({
            id: item.id,
            service_title: item.service?.title || "N/A",
            client_name: item.client?.name || "N/A",
            start_date: item.project_start_date || "N/A",
            end_date: item.project_end_date || "N/A",
            price: item.project_price || item.service?.price || "N/A",
            status: item.status || "Pending",
          }))
        );

        setPagination(response.data.data.pagination);
      }

    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOrderList(pagination.current_page);
  }, []);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    getOrderList(page);
  };

  const updateStatus = async (orderId, status) => {
    try {
      setLoading(true);

      await request({
        url: `orders/${orderId}/status`,
        method: "PUT",
        data: { status },
      });

      toast.success(`Status updated to ${status}`);

      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, status } : order
        )
      );
    } catch {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter((order) =>
    Object.values(order).some((v) =>
      String(v).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <div>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Order</li>
            <li className="breadcrumb-item active">Order List</li>
          </ol>
        </div>
      </div>

      {/* Page Body */}
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
        <div className="container-fluid">
          <div className="card shadow-sm">
            <div className="card-header">
              <h6 className="card-title m-0">Order List</h6>
            </div>

            <div className="card-body">
              {/* Search */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    className="form-control"
                    placeholder="Search orders..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              </div>

              {/* Cards */}
              {loading ? (
                <Loading />
              ) : (
                <div className="row">
                  {filteredOrders.length ? (
                    filteredOrders.map((order) => (
                      <div
                        key={order.id}
                        className="col-xl-4 col-lg-6 mb-4"
                      >
                        <div className="card h-100 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <h6 className="fw-bold mb-1">
                                  {order.service_title}
                                </h6>
                                <p className="mb-1 text-muted">
                                  Client: {order.client_name}
                                </p>
                              </div>

                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  variant="light"
                                  className="border-0 p-0"
                                >
                                  <FaEllipsisV />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                  {["Pending", "Progress", "Done", "Rejected"].map(
                                    (status) => (
                                      <Dropdown.Item
                                        key={status}
                                        onClick={() =>
                                          updateStatus(order.id, status)
                                        }
                                      >
                                        Mark as {status}
                                      </Dropdown.Item>
                                    )
                                  )}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>

                            <hr />

                            <p className="mb-1">
                              📅 Start: <strong>{order.start_date}</strong>
                            </p>
                            <p className="mb-1">
                              ⏳ Deadline: <strong>{order.end_date}</strong>
                            </p>
                            <p className="mb-1">
                              💰 Price: <strong>{order.price}</strong>
                            </p>

                            <Badge bg={STATUS_COLORS[order.status]}>
                              {order.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-5">
                      No orders found
                    </div>
                  )}
                </div>
              )}

              {/* Pagination */}
              {pagination.last_page > 1 && (
                <Pagination
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderList;
